import React, { useState } from 'react';
import { Container, Typography, Grid, Box, Card, CardContent, TextField, Button, Snackbar, Alert, CircularProgress } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { notifyInternal } from '../services/email';

const mapStyle = {
  width: '100%',
  height: '450px',
  border: 0,
  borderRadius: '12px',
};

const contactInfo = [
  {
    icon: LocationOnIcon,
    title: 'Our Location',
    description: '102 River Street, Lynn, MA 01905'
  },
  {
    icon: PhoneIcon,
    title: 'Phone Number',
    description: '+1 (617) 751-8481'
  },
  {
    icon: EmailIcon,
    title: 'Email Address',
    description: 'info@akdmetech.com'
  },
  {
    icon: AccessTimeIcon,
    title: 'Business Hours',
    description: 'Mon-Fri: 9AM-6PM, Sat: 10AM-4PM'
  }
];

function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await notifyInternal(
        `Contact Form: ${formData.subject}`,
        `From: ${formData.name} <${formData.email}>\n\n${formData.message}`,
        formData.email,
      );
      setSnackbar({ open: true, message: 'Message sent! We will get back to you shortly.', severity: 'success' });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to send message. Please call us directly.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: 'background.default', py: 8 }}>
      <Container>
        <Typography 
          variant="h2" 
          align="center" 
          gutterBottom
          className="gradient-text"
          sx={{ mb: 6 }}
        >
          Contact Us
        </Typography>
        <Typography 
          variant="h5" 
          align="center" 
          color="text.secondary" 
          sx={{ mb: 8, maxWidth: 800, mx: 'auto' }}
        >
          Get in touch with our expert team for any inquiries or support
        </Typography>

        <Grid container spacing={4}>
          {/* Contact Information Cards */}
          {contactInfo.map((info, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card 
                className="glass-effect"
                sx={{ 
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(37, 99, 235, 0.2) 100%)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '15px',
                  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)'
                  }
                }}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  {React.createElement(info.icon, { 
                    sx: { 
                      fontSize: 40,
                      color: 'primary.main',
                      mb: 2
                    } 
                  })}
                  <Typography variant="h6" gutterBottom>
                    {info.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {info.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {/* Contact Form */}
          <Grid item xs={12}>
            <Card 
              className="glass-effect"
              sx={{ 
                mt: 4,
                background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(37, 99, 235, 0.1) 100%)',
                backdropFilter: 'blur(10px)',
                borderRadius: '15px',
                boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom className="gradient-text" align="center">
                  Send Us a Message
                </Typography>
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 4 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        variant="outlined"
                        required
                        sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' } } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        variant="outlined"
                        required
                        sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' } } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        variant="outlined"
                        required
                        sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' } } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        multiline
                        rows={4}
                        variant="outlined"
                        required
                        sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' } } }}
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ textAlign: 'center' }}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={loading}
                        sx={{
                          mt: 2, px: 6, py: 1.5,
                          background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
                          color: 'background.default',
                          fontWeight: 600,
                          '&:hover': { background: 'linear-gradient(135deg, var(--primary) 20%, var(--secondary) 80%)' }
                        }}
                      >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Message'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Map Section */}
        <Card 
          className="glass-effect"
          sx={{ 
            mt: 8,
            background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(37, 99, 235, 0.1) 100%)',
            backdropFilter: 'blur(10px)',
            borderRadius: '15px',
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom className="gradient-text" align="center" sx={{ mb: 4 }}>
              Visit Our Location
            </Typography>
            <Box sx={{ borderRadius: '12px', overflow: 'hidden' }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2944.8685529633183!2d-70.94541899999999!3d42.466751!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89e36d73ea5c6e2b%3A0x6de9d978f9a3e7e0!2s102%20River%20St%2C%20Lynn%2C%20MA%2001905!5e0!3m2!1sen!2sus!4v1709765156276!5m2!1sen!2sus"
                style={mapStyle}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Our Location"
              />
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default Contact;
